import { useMemo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTour } from '../../contexts/TourContext'
import { IconDashboard, IconPackage, IconMoney, IconCalendar, IconChart, IconSettings, IconHome, IconPlay, IconCheck, IconUsers, IconShield } from '../Icons'
import './TourGuide.css'

// Маппинг иконок
const STEP_ICONS = {
  home: <IconHome size={26} />,
  dashboard: <IconDashboard size={26} />,
  settings: <IconSettings size={26} />,
  users: <IconUsers size={26} />,
  money: <IconMoney size={26} />,
  package: <IconPackage size={26} />,
  chart: <IconChart size={26} />,
  calendar: <IconCalendar size={26} />,
  shield: <IconShield size={26} />,
  check: <IconCheck size={26} style={{ color: '#10b981' }} />,
}

// Расчёт позиции тултипа относительно целевого элемента
function calcTooltipPos(targetRect, tooltipW = 380, tooltipH = 220) {
  if (!targetRect) {
    return {
      style: {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      placement: 'center',
    }
  }

  const pad = 16
  const gap = 16
  const vw = window.innerWidth
  const vh = window.innerHeight

  const cx = targetRect.left + targetRect.width / 2
  const cy = targetRect.top + targetRect.height / 2

  // Снизу
  if (targetRect.top + targetRect.height + gap + tooltipH + pad < vh) {
    return {
      style: {
        top: targetRect.top + targetRect.height + gap,
        left: Math.max(pad, Math.min(cx - tooltipW / 2, vw - tooltipW - pad)),
      },
      placement: 'bottom',
    }
  }

  // Сверху
  if (targetRect.top - gap - tooltipH - pad > 0) {
    return {
      style: {
        top: targetRect.top - gap - tooltipH,
        left: Math.max(pad, Math.min(cx - tooltipW / 2, vw - tooltipW - pad)),
      },
      placement: 'top',
    }
  }

  // Справа
  if (targetRect.left + targetRect.width + gap + tooltipW + pad < vw) {
    return {
      style: {
        top: Math.max(pad, Math.min(cy - tooltipH / 2, vh - tooltipH - pad)),
        left: targetRect.left + targetRect.width + gap,
      },
      placement: 'right',
    }
  }

  // Слева
  return {
    style: {
      top: Math.max(pad, Math.min(cy - tooltipH / 2, vh - tooltipH - pad)),
      left: Math.max(pad, targetRect.left - gap - tooltipW),
    },
    placement: 'left',
  }
}

export default function TourGuide() {
  const {
    isActive,
    currentStep,
    steps,
    targetRect,
    nextStep,
    prevStep,
    goToStep,
    endTour,
    progress,
    activeTargetSelector,
  } = useTour()

  const prevTargetRef = useRef(null)
  const tooltipRef = useRef(null)
  const [tooltipSize, setTooltipSize] = useState({ width: 380, height: 220 })

  // Управляем data-tour-active атрибутом на целевом DOM-элементе
  useEffect(() => {
    // Снимаем с предыдущего
    if (prevTargetRef.current) {
      prevTargetRef.current.removeAttribute('data-tour-active')
      prevTargetRef.current = null
    }

    if (!isActive || !activeTargetSelector) return

    // Поиск по новой адаптивной функции
    const elements = document.querySelectorAll(activeTargetSelector)
    let el = null
    for (const item of elements) {
      const r = item.getBoundingClientRect()
      if (r.width > 0 && r.height > 0 && window.getComputedStyle(item).display !== 'none') {
        el = item
        break
      }
    }
    if (!el) el = elements[0]

    if (el) {
      el.setAttribute('data-tour-active', 'true')
      prevTargetRef.current = el
    }

    return () => {
      if (prevTargetRef.current) {
        prevTargetRef.current.removeAttribute('data-tour-active')
        prevTargetRef.current = null
      }
    }
  }, [isActive, activeTargetSelector])

  // Замеряем размеры прыгающего тултипа для позиционирования
  useEffect(() => {
    if (isActive && tooltipRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect
          if (width > 0 && height > 0) {
            setTooltipSize({ width: width + 44, height: height + 44 }) // Учитываем padding
          }
        }
      })
      resizeObserver.observe(tooltipRef.current)
      return () => resizeObserver.disconnect()
    }
  }, [isActive, currentStep, targetRect])

  // Cleanup при размонтировании
  useEffect(() => {
    return () => {
      document.querySelectorAll('[data-tour-active]').forEach(el => {
        el.removeAttribute('data-tour-active')
      })
    }
  }, [])

  const step = steps[currentStep]
  const isFirst = currentStep === 0
  const isLast = currentStep === steps.length - 1
  const hasTarget = !!targetRect

  const { style: tooltipStyle, placement } = useMemo(
    () => calcTooltipPos(targetRect, tooltipSize.width, tooltipSize.height),
    [targetRect, tooltipSize]
  )

  if (!isActive || !step) return null

  return createPortal(
    <>
      {/* Spotlight прожектор — рендерится на уровне body в портале для предотвращения обрезки overflow */}
      {hasTarget && (
        <div 
          className="tour-spotlight"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 12,
          }}
        />
      )}

      {/* Кнопка X — всегда видна */}
      <button className="tour-btn tour-btn--skip" onClick={endTour} title="Пропустить обучение">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Backdrop — только когда нет target (welcome/complete шаги) */}
      {!hasTarget && (
        <div className="tour-backdrop" onClick={endTour} />
      )}

      {/* Окошко с Описанием (прыгает около объекта) */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip-info tour-tooltip-info--${hasTarget ? placement : 'center'}`}
        style={hasTarget ? tooltipStyle : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="tour-icon" key={step.id}>
          {STEP_ICONS[step.icon] || STEP_ICONS[step.id] || <IconHome size={26} />}
        </div>

        {/* Title */}
        <div className="tour-title">{step.title}</div>

        {/* Content */}
        <div className="tour-content">{step.content}</div>

        {/* Example tip (если есть) */}
        {step.example && (
          <div className="tour-example" dangerouslySetInnerHTML={{ __html: step.example }} />
        )}

        {/* Tab badge */}
        {step.tab && (
          <div className="tour-tab-badge">
            <span className="tour-tab-badge-dot" />
            Раздел: {
              step.tab === 'dashboard' ? 'Дашборд' : 
              step.tab === 'operations' ? 'Операции' : 
              step.tab === 'payments' ? 'Зарплаты' : 
              step.tab === 'schedule' ? 'Расписание' : 
              step.tab === 'analytics' ? 'Аналитика' : 
              step.tab === 'settings' ? 'Настройки' : step.tab
            }
          </div>
        )}
      </div>

      {/* Железобетонная статичная панель управления туром (всегда внизу экрана) */}
      <div className="tour-control-panel" onClick={(e) => e.stopPropagation()}>
        <div className="tour-control-nav">
          {/* Left Column */}
          <div className="tour-control-col tour-control-col--left">
            <button
              className="tour-btn tour-btn--prev"
              onClick={prevStep}
              style={{ display: isFirst ? 'none' : 'inline-flex' }}
            >
              ← Назад
            </button>
            {!isLast && (
              <button
                className="tour-btn tour-btn--skip-text"
                onClick={endTour}
                title="Пропустить обучение"
              >
                Пропустить
              </button>
            )}
          </div>
          
          {/* Center Column */}
          <div className="tour-control-col tour-control-col--center">
            {/* Dots */}
            <div className="tour-dots">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  className={`tour-dot ${idx === currentStep ? 'tour-dot--active' : idx < currentStep ? 'tour-dot--passed' : ''}`}
                  onClick={() => goToStep(idx)}
                />
              ))}
            </div>

            <span className="tour-counter">
              {currentStep + 1} / {steps.length}
            </span>
          </div>

          {/* Right Column */}
          <div className="tour-control-col tour-control-col--right">
            <button
              className={`tour-btn ${isLast ? 'tour-btn--complete' : 'tour-btn--next'}`}
              onClick={nextStep}
            >
              {isLast ? '✓ Готово!' : 'Далее →'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
